/**
 * Segment CSV export — field whitelist + safe serialization.
 *
 * Pure, dependency-free so it is unit-testable in isolation (lists.service.ts consumes it).
 *
 * SAFETY:
 * - EXPORTABLE_FIELDS is the ONLY set of columns that can appear in an export. A caller-supplied
 *   field key that isn't whitelisted is silently dropped — arbitrary names never reach a CSV header
 *   or a Supabase select. Every key is a verified column on public.contacts.
 * - csvEscape neutralizes spreadsheet formula-injection (leading = + - @ tab CR) and RFC-4180-quotes
 *   values containing comma / quote / LF / CR.
 */

export interface ExportField {
  key: string;
  label: string;
}

export const EXPORTABLE_FIELDS: ExportField[] = [
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone_number", label: "Phone" },
  { key: "contact_type", label: "Contact Type" },
  { key: "member_status", label: "Member Status" },
  { key: "membership_id", label: "Membership ID" },
  { key: "membership_tier", label: "Membership Tier" },
  { key: "is_usls_member", label: "USLS Member" },
  { key: "has_active_subscription", label: "Active Subscription" },
  { key: "is_primary_member", label: "Primary Member" },
  { key: "paying_member", label: "Paying Member" },
  { key: "member_date", label: "Member Date" },
  { key: "member_start_date", label: "Member Start Date" },
  { key: "member_end_date", label: "Member End Date" },
  { key: "member_renewal_date", label: "Member Renewal Date" },
  { key: "member_expiration_date", label: "Member Expiration Date" },
  { key: "address_line1", label: "Address Line 1" },
  { key: "address_line2", label: "Address Line 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "postal_code", label: "Postal Code" },
  { key: "country", label: "Country" },
  { key: "email_opt_in", label: "Email Opt-In" },
  { key: "sms_opt_in", label: "SMS Opt-In" },
  { key: "do_not_call", label: "Do Not Call" },
  { key: "source", label: "Source" },
  { key: "tags", label: "Tags" },
  { key: "product_tags", label: "Product Tags" },
  { key: "date_of_birth", label: "Date of Birth" },
  { key: "created_at", label: "Created At" },
  { key: "updated_at", label: "Updated At" },
];

/** Default columns when the caller doesn't specify fields (preserves the original export shape). */
export const DEFAULT_EXPORT_FIELD_KEYS: string[] = [
  "first_name",
  "last_name",
  "email",
  "phone_number",
  "contact_type",
  "state",
  "city",
  "postal_code",
  "member_status",
  "tags",
  "created_at",
];

/*
 * requiresServerSegmentCsv() and SERVER_ONLY_SEGMENT_OPERATORS were removed deliberately
 * (2026-09-02). They diverted only proximity segments to the server compiler and left every other
 * dynamic segment to a browser-side reimplementation over physical `contacts` columns, which broke
 * on compiler-backed virtual fields such as email_quality and opened_email_30d and risked silent
 * membership divergence from preview/send. ALL dynamic segments now export through
 * export-segment-csv, so there is no allow-list to keep in step with the compiler.
 */

const EXPORTABLE_FIELD_MAP: Map<string, ExportField> = new Map(
  EXPORTABLE_FIELDS.map((f) => [f.key, f]),
);

/**
 * Resolve caller-requested field keys to whitelisted ExportField defs, preserving the caller's order,
 * de-duplicating, and silently dropping any non-whitelisted key. Falls back to the default set if
 * nothing valid remains.
 */
export function resolveExportColumns(fields?: string[]): ExportField[] {
  const keys = fields && fields.length > 0 ? fields : DEFAULT_EXPORT_FIELD_KEYS;
  const resolved: ExportField[] = [];
  const seen = new Set<string>();
  for (const k of keys) {
    const def = EXPORTABLE_FIELD_MAP.get(k);
    if (def && !seen.has(def.key)) {
      resolved.push(def);
      seen.add(def.key);
    }
  }
  if (resolved.length === 0) {
    return DEFAULT_EXPORT_FIELD_KEYS.map((k) => EXPORTABLE_FIELD_MAP.get(k)!);
  }
  return resolved;
}

export function csvEscape(value: unknown): string {
  if (value === null || value === undefined) {
    return "";
  }
  let s: string;
  if (Array.isArray(value)) {
    s = value.join(", ");
  } else if (typeof value === "object") {
    s = JSON.stringify(value);
  } else {
    s = String(value);
  }
  // CSV formula-injection guard: a leading =, +, -, @, tab or CR makes spreadsheets treat the cell
  // as a formula. Prefix a single quote to neutralize.
  if (/^[=+\-@\t\r]/.test(s)) {
    s = "'" + s;
  }
  // RFC-4180 quoting — quote on comma, quote, LF, or CR.
  if (
    s.includes(",") ||
    s.includes('"') ||
    s.includes("\n") ||
    s.includes("\r")
  ) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function rowsToCsv(
  rows: Array<Record<string, unknown>>,
  columns: ExportField[],
): string {
  const header = columns.map((c) => csvEscape(c.label)).join(",");
  const lines = rows.map((r) =>
    columns.map((col) => csvEscape(r[col.key])).join(","),
  );
  return [header, ...lines].join("\n");
}
